<!-- eslint-disable vue/require-default-prop -->
<script setup lang="ts">
import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useField } from 'vee-validate'
import { type Component, computed, nextTick, ref, useAttrs, watch } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import IconSparkles from '@/components/icons/IconSparkles.vue'
import { translationFunctionDefault } from '@/utilities/translations'

import LoadingSpinner from '../LoadingSpinner.vue'
import TooltipApplier from '../overlays/TooltipApplier.vue'

const props = withDefaults(
  defineProps<{
    // While the AI search runs, the button turns into a cancel button on hover or focus
    aiCancelable?: boolean | undefined
    aiLoading?: boolean | undefined
    autocomplete?: string
    capitalizeFirst?: boolean
    disabled?: boolean
    errorMessage?: string | undefined
    inCreateContext?: boolean
    isFilledByAi?: boolean
    isNumber?: boolean
    isPassword?: boolean
    label?: string
    name?: string
    placeholder?: string
    required?: boolean
    showAiButton?: boolean
    status?: 'error' | 'success' | undefined
  }>(),
  {
    capitalizeFirst: true,
  },
)

const emit = defineEmits<{
  // eslint-disable-next-line vue/define-emits-declaration
  (e: 'aiClick'): void
  // eslint-disable-next-line vue/define-emits-declaration,@typescript-eslint/unified-signatures
  (e: 'aiCancel'): void
  // eslint-disable-next-line vue/define-emits-declaration,@typescript-eslint/unified-signatures
  (e: 'manual-input'): void
}>()

defineSlots<{ icon: Component }>()

defineOptions({ inheritAttrs: false })

const inputEl = ref<HTMLInputElement | null>(null)
const showPassword = ref(false)

const toggleShowPassword = (): void => {
  showPassword.value = !showPassword.value
}

const inputType = computed(() => {
  if (props.isPassword) return 'password'
  if (props.isNumber) return 'number'
  return 'text'
})

const { errorMessage: vErrorMessage, value: vValue } = useField<number | string | undefined>(() => {
  return props.name ?? ''
})

const attributesUse = useAttrs()
const { class: classAttr, ...attrs } = attributesUse

const model = defineModel<number | string | undefined>({
  get: (value) => {
    const getterValue = props.name ? vValue.value : value
    return typeof getterValue === 'string' && attrs['type'] === 'url'
      ? getterValue.replace('https://', '').replace('http://', '')
      : getterValue
  },
  set: (value) => {
    if (props.name) {
      vValue.value =
        typeof value === 'string' && attrs['type'] === 'url' && !value.startsWith('http')
          ? `https://${value}`
          : value
    }
    return value
  },
})

const inputTextClass = computed(() => (props.isFilledByAi ? 'text-lochmara-600 italic' : ''))

watch(
  () => vValue.value,
  (newValue) => {
    model.value = newValue
  },
)

const displayError = computed(() => props.errorMessage ?? vErrorMessage.value)
const computedStatus = computed<typeof props.status>(() =>
  displayError.value ? 'error' : props.status,
)

const focusInput = (): void => {
  if (inputEl.value) inputEl.value.focus()
}

const onInput = async (event: Event): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const target = event.target as HTMLInputElement
  const { value } = target

  if (props.name) {
    vValue.value = value
  } else {
    model.value = value
  }

  // eslint-disable-next-line vue/custom-event-name-casing
  emit('manual-input')

  await nextTick(() => {
    if (props.capitalizeFirst && typeof value === 'string' && value.length > 0) {
      const capitalized = value.charAt(0).toUpperCase() + value.slice(1)
      if (props.name) vValue.value = capitalized
      else model.value = capitalized
      if (inputEl.value) inputEl.value.value = capitalized
    }
  })
}

const tfDefault = translationFunctionDefault()

const isAiCancelable = computed(() => props.aiLoading && props.aiCancelable)

const aiCancelAttributes = computed((): Record<string, string> => {
  if (!isAiCancelable.value) return {}

  const label = tfDefault('editRelationGroup.aiCancel')
  return { 'aria-label': label, title: label }
})

const onAiClick = (): void => {
  if (isAiCancelable.value) {
    emit('aiCancel')
    return
  }
  emit('aiClick')
}
</script>

<template>
  <div
    class="relative rounded-lg border border-black-200 bg-white pb-3 pl-5 pt-8 text-sm"
    :class="[
      classAttr,
      !!displayError || !!isPassword ? 'pr-9' : 'pr-5',
      computedStatus === 'error' ? 'border-radical_red-500' : '',
      computedStatus === 'success' ? 'border-keppel-500' : '',
      disabled
        ? 'cursor-not-allowed border-black-100 bg-black-100'
        : computedStatus === undefined
          ? 'cursor-text hover:border-lochmara-600'
          : 'cursor-text',
    ]"
    :error="displayError ? 'true' : 'false'"
    @click="focusInput"
  >
    <label
      v-if="label"
      class="absolute left-5 top-3 z-10 text-xs font-medium text-black-400"
      :title="label"
      >{{ label }} {{ required ? '*' : '' }}
    </label>

    <button
      v-if="showAiButton"
      v-bind="aiCancelAttributes"
      class="ai-button group"
      :class="{ 'absolute -bottom-3 -right-2 z-50': aiLoading }"
      :data-testid="TEST_IDS_CREATE.textInput.aiButton"
      type="button"
      @click="onAiClick"
    >
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <span v-if="!aiLoading">AI</span>
      <template v-else>
        <LoadingSpinner
          :class="{ 'group-hover:hidden group-focus-visible:hidden': isAiCancelable }"
          :data-testid="TEST_IDS_CREATE.textInput.aiSpinner"
          size="0.75rem"
        />
        <XMarkIcon
          v-if="isAiCancelable"
          aria-hidden="true"
          class="hidden size-4 group-hover:block group-focus-visible:block"
        />
      </template>
    </button>
    <IconSparkles v-if="isFilledByAi" class="absolute bottom-3 right-2 size-5 text-lochmara-600" />
    <input
      ref="inputEl"
      v-bind="$attrs"
      v-model="model"
      :autocomplete="autocomplete || 'off'"
      class="bg-inherit w-full border-none p-0 text-sm placeholder:italic placeholder:text-black-300 focus:ring-0"
      :class="[
        disabled ? 'cursor-not-allowed text-black-400' : 'cursor-text text-black-900',
        inputTextClass,
      ]"
      :disabled="!!disabled"
      :has-error="!!displayError || !!vErrorMessage"
      :placeholder="placeholder || ''"
      :type="showPassword ? 'text' : inputType"
      @input="onInput"
    />

    <div
      class="absolute right-5 top-3 h-3 w-3 cursor-pointer text-black-400 empty:pointer-events-none"
    >
      <slot name="icon"></slot>
    </div>

    <div class="absolute bottom-4 right-2">
      <TooltipApplier
        v-if="!!displayError"
        :align="inCreateContext ? 'right' : 'center'"
        :data-testid="`${attributesUse['data-testid']}-error`"
        is-red
        :label="displayError"
      >
        <ExclamationCircleIcon class="h-4 w-4 text-radical_red-500" />
      </TooltipApplier>
    </div>

    <EyeIcon
      v-if="isPassword && !showPassword"
      class="absolute right-5 top-3 h-4 w-4 cursor-pointer text-black-400"
      @click="toggleShowPassword"
    />
    <EyeSlashIcon
      v-if="isPassword && showPassword"
      class="absolute right-5 top-3 h-4 w-4 cursor-pointer text-black-400"
      @click="toggleShowPassword"
    />
  </div>
</template>

<style scoped>
.ai-button {
  position: absolute;
  bottom: 6px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  font-weight: medium;
  font-size: 14px;
  color: #007ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image:
    linear-gradient(rgb(var(--tw-surface-white-DEFAULT)), rgb(var(--tw-surface-white-DEFAULT))),
    linear-gradient(135deg, #cae8ff, #007ddd);
  background-origin: border-box;
  background-clip: content-box, border-box;
  border: 3px solid transparent;
  box-sizing: border-box;
  cursor: pointer;
  z-index: 20;
}
</style>
